import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WelcomeView } from './components/WelcomeView';
import { LoginView } from './components/LoginView';
import { RegisterView } from './components/RegisterView';
import { FitSessionView } from './components/FitSessionView';
import { ViewType, FormData, ModalProps } from './types';
 

export const FitSessionModal: React.FC<ModalProps> = ({
  retailerData,
  productData,
  apiUrl,
  category,
  context,
  brandingHideLogo
}) => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    try {
      const s = sessionStorage.getItem('fit_guest_selected') === 'true';
      const p = localStorage.getItem('fit_guest_selected_persist') === 'true';
      return (s || p) ? 'fit-session' : 'welcome';
    } catch { return 'welcome'; }
  });
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authMethod, setAuthMethod] = useState<'google' | 'facebook' | null>(null);
  const [authPopupBlocked, setAuthPopupBlocked] = useState(false);
  const authPopupRef = useRef<Window | null>(null);
  const authBCRef = useRef<BroadcastChannel | null>(null);
  const openOAuthPopup = (provider: 'google' | 'facebook') => {
    try {
      setAuthBusy(true);
      setAuthMethod(provider);
      setAuthPopupBlocked(false);
      const base = (apiUrl || '').replace(/\/+$/, '');
      const wantsOrigin = window.location.origin;
      const nonce = (() => {
        try {
          const arr = new Uint32Array(4);
          if (window.crypto && window.crypto.getRandomValues) {
            window.crypto.getRandomValues(arr);
            return Array.from(arr).map(n=>n.toString(16)).join('');
          }
        } catch {}
        return Math.random().toString(36).slice(2) + Date.now().toString(36);
      })();
      try { sessionStorage.setItem('fit_sso_nonce', nonce); } catch {}
      const nextPath = `/api/auth-supabase/sso-bridge?target=${encodeURIComponent(wantsOrigin)}&n=${encodeURIComponent(nonce)}`;
      const startUrl = `${base}/api/auth-supabase/oauth/${provider}/start?type=consumer&next=${encodeURIComponent(nextPath)}`;
      // Prepare BroadcastChannel fallback (mobile browsers sometimes lack window.opener)
      const chName = `fit_sso_${nonce}`;
      try {
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel(chName);
          authBCRef.current = bc;
        } else {
          authBCRef.current = null;
        }
      } catch { authBCRef.current = null; }
      const popup = window.open(
        startUrl,
        'fit_oauth',
        'width=520,height=640,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes'
      );
      authPopupRef.current = popup || null;
      if (!popup) setAuthPopupBlocked(true);
      let resolved = false;
      let pollId: any = null;
      const attemptResume = async () => {
        try {
          const baseUrl = (apiUrl || '').replace(/\/+$/, '');
          const r1 = await fetch(baseUrl + '/api/consumer/profile', { credentials: 'include' });
          const j1 = await r1.json().catch(() => ({} as any));
          if (j1 && j1.success) {
            const r2 = await fetch(baseUrl + '/api/auth-supabase/mint-widget-token', { method: 'POST', credentials: 'include' });
            const j2 = await r2.json().catch(() => ({} as any));
            if (j2 && j2.success && j2.token) {
              try {
                sessionStorage.setItem('fit_token', j2.token);
                sessionStorage.setItem('fit_user_token', j2.token);
                if (j1.profile) sessionStorage.setItem('fit_user', JSON.stringify(j1.profile));
                sessionStorage.setItem('fit_logged_in', 'true');
              } catch {}
              setCurrentView('fit-session');
              return true;
            }
          }
        } catch {}
        return false;
      };
      const cleanup = () => {
        try { window.removeEventListener('message', onMessage as any); } catch {}
        try { if (authPopupRef.current && !authPopupRef.current.closed) authPopupRef.current.close(); } catch {}
        try { if (authBCRef.current) { authBCRef.current.close(); } } catch {}
        authPopupRef.current = null;
        authBCRef.current = null;
        try { if (pollId) { clearInterval(pollId); pollId = null; } } catch {}
        setAuthBusy(false);
        setAuthMethod(null);
      };
      const onMessage = (e: MessageEvent) => {
        try {
          const d: any = e.data;
          if (!d || typeof d !== 'object' || d.type !== 'FIT_SSO_BRIDGE') return;
          // Nonce check to prevent accepting messages from wrong origins
          const expected = (() => { try { return sessionStorage.getItem('fit_sso_nonce') || ''; } catch { return ''; } })();
          if (!d.nonce || !expected || String(d.nonce) !== String(expected)) return;
          if (resolved) return;
          if (d.status === 'ok' && d.token) {
            resolved = true;
            cleanup();
            try {
              sessionStorage.setItem('fit_token', d.token);
              sessionStorage.setItem('fit_user_token', d.token);
              if (d.user) sessionStorage.setItem('fit_user', JSON.stringify(d.user));
              sessionStorage.setItem('fit_logged_in', 'true');
              try { window.dispatchEvent(new CustomEvent('fit-user-updated', { detail: d.user } as any)); } catch {}
              try { window.parent.postMessage({ type: 'fit-user-updated', user: d.user }, '*'); } catch {}
            } catch {}
            setCurrentView('fit-session');
          } else if (d.status === 'no_session') {
            // Ignore; wait for popup to complete or polling fallback
            return;
          } else {
            resolved = true;
            cleanup();
            setAuthBusy(false);
            setCurrentView('login');
          }
        } catch {}
      };
      window.addEventListener('message', onMessage);
      // BroadcastChannel listener
      try {
        if (authBCRef.current) {
          const bc = authBCRef.current;
          bc.onmessage = (ev: MessageEvent) => {
            try {
              const d: any = ev.data;
              if (!d || typeof d !== 'object' || d.type !== 'FIT_SSO_BRIDGE') return;
              const expected = (() => { try { return sessionStorage.getItem('fit_sso_nonce') || ''; } catch { return ''; } })();
              if (!d.nonce || !expected || String(d.nonce) !== String(expected)) return;
              if (resolved) return;
              if (d.status === 'ok' && d.token) {
                resolved = true;
                cleanup();
                try {
                  sessionStorage.setItem('fit_token', d.token);
                  sessionStorage.setItem('fit_user_token', d.token);
                  if (d.user) sessionStorage.setItem('fit_user', JSON.stringify(d.user));
                  sessionStorage.setItem('fit_logged_in', 'true');
                  try { window.dispatchEvent(new CustomEvent('fit-user-updated', { detail: d.user } as any)); } catch {}
                  try { window.parent.postMessage({ type: 'fit-user-updated', user: d.user }, '*'); } catch {}
                } catch {}
                setCurrentView('fit-session');
              } else if (d.status === 'no_session') {
                return;
              } else {
                resolved = true;
                cleanup();
                setAuthBusy(false);
                setCurrentView('login');
              }
            } catch {}
          };
        }
      } catch {}
      // Poll for popup closed and resume session without postMessage
      try {
        pollId = setInterval(async () => {
          try {
            if (resolved) { clearInterval(pollId); pollId = null; return; }
            if (authPopupRef.current && authPopupRef.current.closed) {
              const ok = await attemptResume();
              resolved = true;
              cleanup();
              if (!ok) {
                setAuthBusy(false);
                setCurrentView('login');
              }
            }
          } catch {}
        }, 500);
      } catch {}
      // Additionally, try auto-resume a few times regardless of popup.closed (mobile safety)
      try {
        let retries = 0;
        const maxRetries = 8; // ~8s
        const t = setInterval(async () => {
          try {
            if (resolved) { clearInterval(t); return; }
            retries++;
            const ok = await attemptResume();
            if (ok) {
              resolved = true;
              cleanup();
              clearInterval(t);
            } else if (retries >= maxRetries) {
              clearInterval(t);
            }
          } catch { /* ignore */ }
        }, 1000);
      } catch {}
      setTimeout(() => { if (!resolved) { resolved = true; cleanup(); } }, 120000);
    } catch {}
  };
  // Auto-forward if user already logged in (HttpOnly cookie)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // If user explicitly chose guest, skip profile fetch to avoid 401 noise,
        // UNLESS we know we are logged in from a previous session
        const isGuest = (() => { try { return sessionStorage.getItem('fit_guest_selected') === 'true'; } catch { return false; } })();
        const persistedLoggedIn = (() => { try { return sessionStorage.getItem('fit_logged_in') === 'true'; } catch { return false; } })();
        if (!isGuest || persistedLoggedIn) {
          // Logged-in profile (to auto-fill pasPhoto_front)
          const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const r = await fetch(`${apiUrl}/api/consumer/profile`, { credentials: 'include', headers });
          const j = await r.json().catch(() => ({}));
          if (!cancelled && j && j.success) {
            setCurrentView('fit-session');
            try {
              // Clear guest flag
              sessionStorage.removeItem('fit_guest_selected');
              // Dispatch local event so the wizard in this iframe updates instantly
              window.dispatchEvent(new CustomEvent('fit-user-updated', { detail: j.profile } as any));
              // Also inform parent so the Add button can enable immediately
              try {
                const tok = sessionStorage.getItem('fit_user_token') || '';
                if (tok) {
                  window.parent.postMessage({ type: 'fit-auth', token: tok, user: j.profile }, '*');
                } else {
                  window.parent.postMessage({ type: 'fit-user-updated', user: j.profile }, '*');
                }
              } catch {}
            } catch {}
          }
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [apiUrl]);

  // Inform parent window when the modal inside the iframe is ready so it can hide any parent-side loader
  useEffect(() => {
    try {
      window.parent.postMessage({ type: 'FIT_MODAL_READY' }, '*');
    } catch {}
  }, []);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiUrl}/api/auth-supabase/login/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(t('auth.loginSuccess'));
        try {
          const token = result?.data?.token;
          if (token) sessionStorage.setItem('fit_user_token', token);
          // Persist lightweight user info for the wizard and future sessions
          if (result?.data?.user) {
            sessionStorage.setItem('fit_user', JSON.stringify(result.data.user));
          }
          sessionStorage.setItem('fit_logged_in', 'true');
        } catch {}
        
        // FIXED: Use postMessage instead of direct parent window access
        try {
          window.parent.postMessage({ 
            type: 'FIT_SESSION_STARTED', 
            sessionId: result.data?.sessionId || 'session-' + Date.now(),
            user: result.data?.user
          }, '*');
          // Inform wizard explicitly
          window.parent.postMessage({ type: 'fit-user-updated', user: result.data?.user }, '*');
          // Also dispatch a local event so the wizard in this iframe updates instantly
          try { window.dispatchEvent(new CustomEvent('fit-user-updated', { detail: result.data?.user } as any)); } catch {}
        } catch (e) {
          console.log('PostMessage not available, continuing...');
        }
        
        setTimeout(() => {
          setCurrentView('fit-session');
        }, 1500);
      } else {
        setError(result.message || t('auth.loginFailed'));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/auth-supabase/register/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(t('auth.registerSuccess'));
        try {
          const token = result?.data?.token;
          if (token) sessionStorage.setItem('fit_user_token', token);
          if (result?.data?.user) {
            sessionStorage.setItem('fit_user', JSON.stringify(result.data.user));
          }
          sessionStorage.setItem('fit_logged_in', 'true');
        } catch {}
        
        // FIXED: Use postMessage instead of direct parent window access
        try {
          window.parent.postMessage({ 
            type: 'FIT_SESSION_STARTED', 
            sessionId: result.data?.sessionId || 'session-' + Date.now(),
            user: result.data?.user
          }, '*');
          window.parent.postMessage({ type: 'fit-user-updated', user: result.data?.user }, '*');
        } catch (e) {
          console.log('PostMessage not available, continuing...');
        }
        
        setTimeout(() => {
          setCurrentView('fit-session');
        }, 1500);
      } else {
        setError(result.message || t('auth.registerFailed'));
      }
    } catch (error) {
      console.error('Register error:', error);
      setError(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  // Remove initial inline loader once an actual React view is visible
  useEffect(() => {
    try {
      if (currentView === 'welcome' || currentView === 'login' || currentView === 'register' || currentView === 'fit-session') {
        const el = document.getElementById('fit-initial-loader');
        if (el) el.remove();
      }
    } catch {}
  }, [currentView]);

  // Live detect login completion while login/register is visible or while auth popup is busy
  useEffect(() => {
    let cancelled = false;
    let timer: any = null;
    const checkAndResume = async () => {
      try {
        const baseUrl = (apiUrl || '').replace(/\/+$/, '');
        const r1 = await fetch(baseUrl + '/api/consumer/profile', { credentials: 'include' });
        const j1 = await r1.json().catch(() => ({} as any));
        if (j1 && j1.success) {
          const r2 = await fetch(baseUrl + '/api/auth-supabase/mint-widget-token', { method: 'POST', credentials: 'include' });
          const j2 = await r2.json().catch(() => ({} as any));
          if (j2 && j2.success && j2.token) {
            if (cancelled) return true;
            try {
              sessionStorage.setItem('fit_token', j2.token);
              sessionStorage.setItem('fit_user_token', j2.token);
              if (j1.profile) sessionStorage.setItem('fit_user', JSON.stringify(j1.profile));
              sessionStorage.setItem('fit_logged_in', 'true');
              window.dispatchEvent(new CustomEvent('fit-user-updated', { detail: j1.profile } as any));
            } catch {}
            setCurrentView('fit-session');
            setAuthBusy(false);
            return true;
          }
        }
      } catch {}
      return false;
    };
    const kickoff = () => {
      try { if (timer) { clearInterval(timer); timer = null; } } catch {}
      let attempts = 0;
      timer = setInterval(async () => {
        if (cancelled) { try { clearInterval(timer); } catch {}; return; }
        attempts++;
        const ok = await checkAndResume();
        if (ok || attempts >= 30) { try { clearInterval(timer); } catch {}; timer = null; }
      }, 1000);
    };
    if (authBusy || currentView === 'login' || currentView === 'register') {
      kickoff();
      const onFocus = () => kickoff();
      const onVis = () => { if (document.visibilityState === 'visible') kickoff(); };
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVis);
      return () => {
        cancelled = true;
        try { if (timer) clearInterval(timer); } catch {}
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('visibilitychange', onVis);
      };
    }
    return () => {};
  }, [authBusy, currentView, apiUrl]);

  // Immediately switch to fit-session when any part of the app dispatches 'fit-user-updated'
  useEffect(() => {
    const onUserUpdated = () => { setCurrentView('fit-session'); setAuthBusy(false); };
    window.addEventListener('fit-user-updated' as any, onUserUpdated as any);
    return () => { window.removeEventListener('fit-user-updated' as any, onUserUpdated as any); };
  }, []);

  // Allow child views (wizard) to request switching to login view
  useEffect(() => {
    const onCustom = () => {
      try { sessionStorage.removeItem('fit_guest_selected'); } catch {}
      setCurrentView('login');
    };
    const onMessage = (e: MessageEvent) => {
      if (e && typeof e.data === 'object' && e.data && (e.data as any).type === 'fit-open-login') onCustom();
    };
    window.addEventListener('fit-open-login' as any, onCustom as any);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('fit-open-login' as any, onCustom as any);
      window.removeEventListener('message', onMessage);
    };
  }, []);

  return (
    <div className={'min-h-screen bg-transparent relative'}>
      {authBusy && (
        <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white shadow rounded-lg p-6 text-center max-w-sm w-full mx-4">
            <div className="mx-auto mb-3 h-5 w-5 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
            <div className="text-slate-800 text-sm mb-1">{t('auth.loggingIn')}</div>
            {authMethod && (
              <div className="text-slate-500 text-xs mb-3">{authMethod === 'google' ? 'Google' : 'Facebook'}</div>
            )}
            {authPopupBlocked && (
              <button
                type="button"
                onClick={() => authMethod && openOAuthPopup(authMethod)}
                className="w-full inline-flex items-center justify-center gap-2 border border-gray-300 rounded-md py-2 px-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 mb-2"
              >
                Opnieuw openen
              </button>
            )}
            <button
              type="button"
              onClick={() => { try { if (authPopupRef.current && !authPopupRef.current.closed) authPopupRef.current.close(); } catch {} setAuthBusy(false); setAuthMethod(null); }}
              className="w-full inline-flex items-center justify-center rounded-md py-2 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
      {currentView === 'welcome' && (
        <WelcomeView 
          productData={productData}
          onViewChange={setCurrentView}
          category={category}
          apiUrl={apiUrl}
        />
      )}
      {currentView === 'login' && (
        <LoginView
          formData={formData}
          isLoading={isLoading}
          error={error}
          success={success}
          onViewChange={setCurrentView}
          onInputChange={handleInputChange}
          onSubmit={handleLogin}
          onGoogleLogin={() => openOAuthPopup('google')}
          onFacebookLogin={() => openOAuthPopup('facebook')}
        />
      )}
      {currentView === 'register' && (
        <RegisterView
          formData={formData}
          isLoading={isLoading}
          error={error}
          success={success}
          onViewChange={setCurrentView}
          onInputChange={handleInputChange}
          onSubmit={handleRegister}
          onGoogleRegister={() => openOAuthPopup('google')}
          onFacebookRegister={() => openOAuthPopup('facebook')}
        />
      )}
      {currentView === 'fit-session' && (
        <FitSessionView
          productData={productData}
          retailerData={retailerData}
          apiUrl={apiUrl}
          context={context}
          brandingHideLogo={brandingHideLogo}
        />
      )}
    </div>
  );
};
